import React, { useState, useEffect } from "react";
import "../styles/RecipeCard.css";
import { useIngredients } from "../context/IngredientsProvider";
import { useShoppingList } from "../context/ShoppingListProvider";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaList,
  FaHeart,
  FaRegHeart,
} from "react-icons/fa";
import Notification from "./Notification";
import ingredients from "../data/ingredients";
import { supabase } from "../supabaseClient";

interface Recipe {
  nimi: string;
  "raaka-aineet": string[];
  tarjoaja: string;
  linkki: string;
  kuva: string;
  valmistusaika?: string;
  erityisruokavaliot?: string[];
}

interface RecipeCardProps {
  recipe: Recipe;
}

const RecipeCard: React.FC<RecipeCardProps> = ({ recipe }) => {
  const { shoppingItems, setShoppingItems } = useShoppingList();
  const { selectedIngredients } = useIngredients();

  const [showNotification, setShowNotification] = useState(false);
  const [addedCount, setAddedCount] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteMessage, setFavoriteMessage] = useState("");
  const [showFavoriteNotification, setShowFavoriteNotification] =
    useState(false);

  const pantryValues = selectedIngredients.map((item) =>
    item.value.trim().toLowerCase()
  );
  const currentList = shoppingItems.map((item) =>
    item.name.trim().toLowerCase()
  );

  useEffect(() => {
    const checkIfFavorite = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("favorite_recipes")
        .select("id")
        .eq("user_id", user.id)
        .eq("title", recipe.nimi)
        .maybeSingle();

      if (data) setIsFavorite(true);
    };

    checkIfFavorite();
  }, [recipe.nimi]);

  const getAlreadyAddedCount = () => {
    return recipe["raaka-aineet"].filter((ingredient) =>
      currentList.includes(ingredient.trim().toLowerCase())
    ).length;
  };

  const addMissingIngredientsToShoppingList = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setFavoriteMessage(
        "Kirjaudu sisään lisätäksesi puuttuvat raaka-aineet kauppalistaa."
      );
      setShowFavoriteNotification(true);
      setTimeout(() => setShowFavoriteNotification(false), 8000);
      return;
    }

    const missing = recipe["raaka-aineet"].filter((ingredient) => {
      const value = ingredient.trim().toLowerCase();
      return !pantryValues.includes(value) && !currentList.includes(value);
    });

    const newItems = missing.map((name) => {
      const found = ingredients.find(
        (i) => i.name.trim().toLowerCase() === name.trim().toLowerCase()
      );
      return {
        id: crypto.randomUUID(),
        name,
        label: found?.category.toLowerCase().replace(/\s/g, "-") || "muu",
        category: found?.category || "Muu",
      };
    });

    if (newItems.length > 0) {
      setShoppingItems((prev) => [...prev, ...newItems]);
      setAddedCount(newItems.length);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 6000);
    }
  };

  const toggleFavorite = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setFavoriteMessage("Kirjaudu sisään tallentaaksesi suosikkeja.");
      setShowFavoriteNotification(true);
      setTimeout(() => setShowFavoriteNotification(false), 5000);
      return;
    }

    if (isFavorite) {
      await supabase
        .from("favorite_recipes")
        .delete()
        .eq("user_id", user.id)
        .eq("title", recipe.nimi);
      setIsFavorite(false);
      setFavoriteMessage("Poistettu suosikeista");
    } else {
      await supabase.from("favorite_recipes").insert({
        user_id: user.id,
        title: recipe.nimi,
        linkki: recipe.linkki,
        image_url: recipe.kuva,
        tarjoaja: recipe.tarjoaja,
        valmistusaika: recipe.valmistusaika,
        erityisruokavaliot: recipe.erityisruokavaliot,
        ingredients: recipe["raaka-aineet"],
      });
      setIsFavorite(true);
      setFavoriteMessage("Lisätty suosikkeihin");
    }

    setShowFavoriteNotification(true);
    setTimeout(() => setShowFavoriteNotification(false), 6000);
  };

  const isInStorage = (ingredient: string) => {
    return pantryValues.includes(ingredient.trim().toLowerCase());
  };

  return (
    <div className="recipe-card">
      {showNotification && (
        <Notification
          message={`Lisätty ${addedCount} puuttuvaat raaka-ainetta kauppalistaan`}
          onClose={() => setShowNotification(false)}
        />
      )}

      {showFavoriteNotification && (
        <Notification
          message={favoriteMessage}
          onClose={() => setShowFavoriteNotification(false)}
        />
      )}

      <div className="recipe-image-wrapper">
        <img src={recipe.kuva} alt={recipe.nimi} className="recipe-image" />

        <div className="recipe-tags">
          {recipe.valmistusaika && (
            <span className="recipe-tag tag-time">{recipe.valmistusaika}</span>
          )}
          {recipe.erityisruokavaliot?.map((tag, i) => (
            <span key={i} className="recipe-tag tag-diet">
              {tag}
            </span>
          ))}
        </div>
        <span className="recipe-provider">{recipe.tarjoaja}</span>
      </div>

      <h3>{recipe.nimi}</h3>
      <div className="ingredients-row">
        <ul className="recipe-ingredients">
          {recipe["raaka-aineet"].map((ingredient, index) => {
            const inStorage = isInStorage(ingredient);
            return (
              <li
                key={index}
                className={inStorage ? "in-storage" : "not-in-storage"}
              >
                {ingredient}
                {inStorage ? (
                  <FaCheckCircle className="icon in-storage-icon" />
                ) : (
                  <FaTimesCircle className="icon not-in-storage-icon" />
                )}
              </li>
            );
          })}
        </ul>
        <div className="button-column">
          <button
            className="add-to-shoppinglist-button"
            title="Lisää puuttuvat"
            onClick={addMissingIngredientsToShoppingList}
          >
            <FaList />
            {getAlreadyAddedCount() > 0 && (
              <span className="item-count-badge">{getAlreadyAddedCount()}</span>
            )}
          </button>

          <button
            className="add-to-shoppinglist-button"
            title="Tallenna suosikiksi"
            onClick={toggleFavorite}
          >
            {isFavorite ? <FaHeart /> : <FaRegHeart />}
          </button>
        </div>
      </div>

      <a
        href={recipe.linkki}
        target="_blank"
        rel="noopener noreferrer"
        className="recipe-link"
      >
        Siirry reseptiin
      </a>
    </div>
  );
};

export default RecipeCard;
